import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import type { CourtTemplatePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { buttonVariants } from "@/components/ui/button";
import { Pencil } from "lucide-react";
import { ListBackButton } from "@/components/list-back-button";

export default function CourtTemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [template, setTemplate] = useState<CourtTemplatePublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);

    api
      .getCourtTemplate(id)
      .then((res) => {
        setTemplate(res.template);
      })
      .catch((e) =>
        setError(
          e instanceof api.ApiError
            ? e.message
            : "Failed to load template",
        ),
      )
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <p className="text-muted-foreground" data-page="court-template-detail" data-state="loading">Loading...</p>;
  }

  if (error) {
    return <p className="text-sm text-destructive" data-page="court-template-detail" data-state="error" data-error>{error}</p>;
  }

  if (!template) {
    return <p className="text-muted-foreground" data-page="court-template-detail" data-state="empty">Template not found</p>;
  }

  const canEdit = template._actions.includes("edit");

  return (
    <div className="flex flex-col gap-6" data-page="court-template-detail" data-state="ready">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-region="header">
        <ListBackButton to="/court-templates" label="Templates" />
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">{template.name}</h1>
          <p className="text-muted-foreground">{template.court_type_name}</p>
          {template.is_default && (
            <p className="text-sm text-primary">Default template</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && (
            <Link
              to={`/court-templates/${template.id}/edit`}
              className={buttonVariants({ variant: "outline", size: "sm" })}
              data-action="edit"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit Template
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
